import styled from 'styled-components'

import { media } from '@/theme/common'
import { DefaultBreakpoints } from '@/theme/defaults'

export const PageWrapper = styled.div<{
  $centered: boolean
  $fillHeight: boolean
  $minHeightPx: number
}>(({ theme, $centered, $fillHeight, $minHeightPx }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  // Note: mobile-first - tight at xs, widens up to the original 16px at md and holds there
  // through lg/xl (no separate lg/xl rule since the value doesn't change again above md).
  gap: theme.space['8'],
  height: 'auto',
  margin: $centered ? '0 auto' : undefined,
  maxWidth: $centered ? `${DefaultBreakpoints.xxl}px` : undefined,
  // Note: `min-height` is a JS-measured pixel value (page.tsx's ResizeObserver), not `0` and not
  // CSS `min-content`/`auto` - all three were tried:
  //
  // A flat `0` lets this shrink below what its children truly need whenever an ancestor is too
  // short to fit them, silently overflowing this Page's own box - since the padding below is
  // baked into this box's height, and the overflowing content renders past it, the bottom
  // padding ends up before the page's real end instead of after it.
  //
  // CSS `min-content`/`auto` (computing this Page's true combined need instead of a flat `0`)
  // fixes that, but a descendant several levels down - ui/table's Container, wrapping a
  // scrollable `overflow: auto` Wrapper around the actual rows - has its *actual full row
  // count*, not its explicit 40rem floor, pulled into a `min-content`-driven ancestor's
  // intrinsic-size computation in Chrome (see TableGroup's Card comment for the measured
  // numbers). A table with hundreds of rows blew this Page - and the outer app shell - up to
  // fit every row, instead of scrolling them internally within 40rem/whatever space is
  // available, defeating ui/table's whole point.
  //
  // Real, measured pixels (the actual current rendered height of this Page's own direct
  // children, which already correctly reflects each child's own explicit floor - e.g.
  // TableGroup's Card is never actually smaller than its own measured minimum, row count or
  // not - summed in page.tsx) sidestep the browser's `min-content` behavior entirely, the same
  // way Card's own min-height does one level down.
  minHeight: $fillHeight ? `${$minHeightPx}px` : undefined,
  padding: `${theme.space['16']} ${theme.space['16']} ${theme.space['16']}`,
  width: '100%',

  [media.up('sm')]: {
    gap: theme.space['12'],
    padding: `${theme.space['16']} ${theme.space['24']} ${theme.space['24']}`,
  },
  [media.up('md')]: {
    gap: theme.space['16'],
    padding: `${theme.space['20']} ${theme.space['40']} ${theme.space['40']}`,
  },
}))
